import { useState } from "react";
import { Loader2, Save, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useCustomTypologies, setCustomTypologies, loadCustomTypologies, applyCustomTypology, layoutItem } from "@/lib/customTypologies";
import { ElevationThumb } from "@/components/ElevationThumb";
import { Confirm } from "@/components/settings/Confirm";
import { StarterPackPanel } from "@/components/StarterPackPanel";

const input = "h-9 w-full rounded-md bg-[#0B0F19] border border-slate-700 px-3 text-sm focus:outline-none focus:border-cyan-500 transition-colors";

const SaveForm = ({ item, update }) => {
  const [f, setF] = useState({ label: "", code: "", type: item.type || "window", system: item.profile_system || "", description: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/typologies", { ...f, layout: { columns: item.columns, rows: item.rows, cells: item.cells, spans: item.spans || [], panel_glass: item.panel_glass || {} } });
      setCustomTypologies([...(await loadCustomTypologies()).filter((t) => t.key !== data.key), data]);
      update({ ...item, typology: data.key });
      toast.success(`Typology "${data.label}" saved · available on every item and to the AI`);
      setF({ ...f, label: "", code: "", description: "" });
    } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="grid grid-cols-[120px_1fr] gap-4 p-3 rounded-lg border border-slate-800 bg-[#0d1220]" data-testid="typology-save-form">
      <div className="h-28 rounded-md bg-[#090D16] border border-slate-800 p-2"><ElevationThumb item={item} size={110} /></div>
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_90px_110px] gap-2">
          <input data-testid="typology-label-input" required value={f.label} onChange={set("label")} placeholder="Name, e.g. Single sliding door + fixed" className={input} />
          <input data-testid="typology-code-input" value={f.code} onChange={set("code")} placeholder="Code" className={input} />
          <select data-testid="typology-type-select" value={f.type} onChange={set("type")} className={input}><option value="window">Window</option><option value="door">Door</option></select>
        </div>
        <input data-testid="typology-system-input" value={f.system} onChange={set("system")} placeholder="Profile system" className={input} />
        <input data-testid="typology-desc-input" value={f.description} onChange={set("description")} placeholder="How to recognise it on a drawing (helps the AI), e.g. one sliding shutter passing over a fixed lite" className={input} />
        <button data-testid="typology-save-btn" type="submit" disabled={busy || !f.label.trim()}
          className="h-9 px-4 rounded-md bg-cyan-500 text-slate-950 text-sm font-semibold flex items-center gap-2 hover:bg-cyan-400 transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Save this item's layout as a typology
        </button>
      </div>
    </form>
  );
};

const Row = ({ t, onApply, onDelete }) => (
  <div className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-800 hover:bg-slate-800/30 transition-colors" data-testid={`custom-typology-${t.key}`}>
    <div className="w-16 h-14 rounded bg-[#090D16] border border-slate-800 p-1 shrink-0"><ElevationThumb item={layoutItem(t)} size={60} /></div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-100 truncate">{t.label}{t.code && <span className="ml-2 font-mono text-[10px] text-cyan-300">{t.code}</span>}</p>
      <p className="text-[11px] text-slate-500 truncate">{t.type} · {t.layout.columns.length}×{t.layout.rows.length} panels · {t.system || "—"}{t.created_by ? ` · by ${t.created_by.split("@")[0]}` : ""}</p>
    </div>
    <button data-testid="custom-typology-apply-btn" onClick={onApply} className="h-8 px-2.5 rounded-md border border-slate-700 text-xs text-slate-300 flex items-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 transition-colors"><Wand2 size={13} />Apply</button>
    <button data-testid="custom-typology-delete-btn" onClick={onDelete} title="Delete" className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors"><Trash2 size={14} /></button>
  </div>
);

export const CustomTypologyDialog = ({ item, update, onClose }) => {
  const list = useCustomTypologies();
  const [del, setDel] = useState(null);
  const remove = async () => {
    const t = del;
    setDel(null);
    try { await api.delete(`/typologies/${t.typology_id}`); setCustomTypologies(list.filter((x) => x.key !== t.key)); toast.success(`Deleted "${t.label}"`); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#111827] border-slate-700 text-slate-100" data-testid="custom-typology-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Custom typologies</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Save any layout (panels, openings, fanlights, side lights) as a reusable typology for the whole team. It appears in the typology list and the AI uses it when reading new drawings.</DialogDescription>
        </DialogHeader>
        <SaveForm item={item} update={update} />
        <StarterPackPanel existing={list} />
        <div className="space-y-2 max-h-72 overflow-auto pr-1" data-testid="custom-typology-list">
          {list.map((t) => <Row key={t.key} t={t} onApply={() => { update(applyCustomTypology(item, t)); toast.success(`Applied "${t.label}" to ${item.tag}`); }} onDelete={() => setDel(t)} />)}
          {!list.length && <p className="text-xs text-slate-500 text-center py-4" data-testid="custom-typology-empty">No custom typologies yet.</p>}
        </div>
        <Confirm open={!!del} testid="typology-delete-dialog" title="Delete typology?" action="Delete" onCancel={() => setDel(null)} onConfirm={remove}
          body={`"${del?.label}" will be removed from the list. Items already using it keep their layout.`} />
      </DialogContent>
    </Dialog>
  );
};
